import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { fetchProducts, fetchCollections } from "@/lib/shopify";
import { DEFAULT_SITE_CONFIG } from "@/data/siteConfig";
import ProductCard from "@/components/product/ProductCard";
import OfferTicker from "@/components/home/OfferTicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [cfg, setCfg] = useState(DEFAULT_SITE_CONFIG);
  const [featured, setFeatured] = useState([]);
  const [shopifyCols, setShopifyCols] = useState({}); // handle -> {hero, title, subtitle}

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/site-config`).then(({ data }) => {
      if (alive && data) setCfg({ ...DEFAULT_SITE_CONFIG, ...data });
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    fetchCollections().then((cols) => {
      if (!alive) return;
      const map = {};
      cols.forEach((c) => { map[c.handle] = c; });
      setShopifyCols(map);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await fetchProducts();
        if (!alive) return;
        const wanted = cfg.handPicked?.productHandles || [];
        const picked = wanted.length
          ? wanted.map((h) => list.find((p) => p.handle === h)).filter(Boolean)
          : list.slice(0, 4);
        setFeatured(picked);
      } catch {
        if (alive) setFeatured([]);
      }
    })();
    return () => { alive = false; };
  }, [cfg.handPicked?.productHandles]);

  // Build the bento list DIRECTLY from Shopify collections (any collection
  // added in Shopify Admin auto-appears here). Order = Shopify's return order.
  const bentoCollections = Object.values(shopifyCols).length
    ? Object.values(shopifyCols).map((c) => ({
        handle: c.handle, title: c.title, subtitle: c.subtitle, image: c.hero,
      }))
    : (cfg.collectionsSection?.items || [])
        .filter((c) => c.visible !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

  // Layout split so any collection beyond the first three ("Festive" etc.)
  // stacks in the LEFT column directly below the primary — no empty space.
  //   left  = [0, 3, 4, 5, ...]  (large cards)
  //   right = [1, 2]             (small cards, always max 2)
  const leftLarge = bentoCollections.length > 3
    ? [bentoCollections[0], ...bentoCollections.slice(3)]
    : bentoCollections.slice(0, 1);
  const rightSmall = bentoCollections.slice(1, 3);

  const linkForHandle = (h) => {
    const legacy = { dresses: "/dresses", "new-arrivals": "/new-arrivals", sale: "/sale" };
    return legacy[h] || `/collection/${h}`;
  };

  const collectionNumber = (handle) => {
    const idx = bentoCollections.findIndex((c) => c.handle === handle);
    return String(idx + 1).padStart(2, "0");
  };

  return (
    <>
      {/* HERO */}
      <section data-testid="home-hero" className="relative h-screen w-full">
        <img src={cfg.hero.image} alt="Vastra West editorial" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/25" />
        <div className="relative h-full flex flex-col items-center justify-end pb-24 md:pb-32 text-center text-white px-6">
          <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] italic animate-fade-up">
            {cfg.hero.titleLines.map((line, i) => (
              <span key={i} className="block">{line}</span>
            ))}
          </h1>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 animate-fade-up delay-200">
            <Link to={cfg.hero.ctaPrimary.href} data-testid="hero-new-arrivals-btn" className="overline px-8 py-4 bg-[color:var(--vw-bg)] text-[color:var(--vw-ink)] hover:opacity-90 transition-opacity">
              {cfg.hero.ctaPrimary.label}
            </Link>
            <Link to={cfg.hero.ctaSecondary.href} data-testid="hero-dresses-btn" className="overline px-8 py-4 border border-white text-white hover:bg-white hover:text-[color:var(--vw-ink)] transition-colors">
              {cfg.hero.ctaSecondary.label}
            </Link>
          </div>
        </div>
      </section>

      {/* OFFER TICKER */}
      <OfferTicker config={cfg.offerTicker} />

      {/* COLLECTIONS */}
      {cfg.collectionsSection?.enabled && bentoCollections.length > 0 && (
        <section data-testid="home-collections" className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="overline text-[color:var(--vw-ink-soft)]">{cfg.collectionsSection.overline}</p>
              <h2 className="font-serif-display text-4xl md:text-6xl mt-3 italic">{cfg.collectionsSection.title}</h2>
            </div>
            <Link to="/shop" className="hidden md:inline-flex items-center gap-2 overline hover:opacity-70">
              View All <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* LEFT column — primary + any collections beyond the first three */}
            {leftLarge.length > 0 && (
              <div className="md:col-span-8 grid grid-cols-1 gap-6">
                {leftLarge.map((c) => (
                  <Link key={c.handle} to={linkForHandle(c.handle)} data-testid={`col-${c.handle}`} className="group block">
                    <div className="relative aspect-[16/10] overflow-hidden hover-zoom bg-[color:var(--vw-bg-alt)]">
                      {c.image && <img src={c.image} alt={c.title} className="w-full h-full object-cover" />}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                      <div className="absolute bottom-0 left-0 p-8 md:p-10 text-white">
                        <p className="overline opacity-90">Collection {collectionNumber(c.handle)}</p>
                        <h3 className="font-serif-display text-4xl md:text-6xl italic mt-2">{c.title}</h3>
                        {c.subtitle && <p className="mt-2 text-sm max-w-md opacity-90">{c.subtitle}</p>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
            {/* RIGHT column — up to 2 secondary collections */}
            {rightSmall.length > 0 && (
              <div className="md:col-span-4 grid grid-cols-1 gap-6">
                {rightSmall.map((c) => (
                  <Link key={c.handle} to={linkForHandle(c.handle)} data-testid={`col-${c.handle}`} className="group block">
                    <div className="relative aspect-[4/5] md:aspect-auto md:h-full overflow-hidden hover-zoom bg-[color:var(--vw-bg-alt)]">
                      {c.image && <img src={c.image} alt={c.title} className="w-full h-full object-cover" />}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                      <div className="absolute bottom-0 left-0 p-6 text-white">
                        <p className="overline opacity-90">Collection {collectionNumber(c.handle)}</p>
                        <h3 className="font-serif-display text-3xl md:text-4xl italic mt-1">{c.title}</h3>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* HANDPICKED */}
      {cfg.handPicked?.enabled && (
        <section data-testid="home-featured" className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="overline text-[color:var(--vw-ink-soft)]">{cfg.handPicked.overline}</p>
              <h2 className="font-serif-display text-4xl md:text-6xl mt-3 italic">{cfg.handPicked.title}</h2>
            </div>
            <Link to="/shop" className="overline hover:opacity-70 inline-flex items-center gap-2">
              Shop the Edit <ArrowRight size={14} />
            </Link>
          </div>
          {featured.length === 0 ? (
            <p data-testid="featured-empty" className="text-center py-16 font-serif-display text-2xl italic text-[color:var(--vw-ink-soft)]">
              New pieces coming soon.
            </p>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {featured.map((p, i) => <ProductCard key={p.id || p.handle} product={p} index={i} />)}
            </div>
          )}
        </section>
      )}

      {/* ABOUT PREVIEW */}
      {cfg.aboutPreview?.enabled && (
        <section data-testid="home-about" className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] bg-[color:var(--vw-bg-alt)] overflow-hidden">
              <img src={cfg.aboutPreview.image} alt="Atelier" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="overline text-[color:var(--vw-ink-soft)]">{cfg.aboutPreview.overline}</p>
            <h2 className="font-serif-display text-4xl md:text-6xl italic mt-4 leading-[1.05]">
              {cfg.aboutPreview.title}
            </h2>
            <p className="mt-6 text-[color:var(--vw-ink-soft)] leading-relaxed max-w-lg">
              {cfg.aboutPreview.body}
            </p>
            <Link to="/about" className="inline-block mt-8 overline border-b border-[color:var(--vw-ink)] pb-1 hover:opacity-70">
              Read our story
            </Link>
          </div>
        </section>
      )}
    </>
  );
};

export default Home;
